import { DOMAIN_SHAPE, FULL_CUT, PUDDLE_CELL_PX, RIM_CUT, fieldAt, shapeOf, unit, type Puddle } from "./puddle-shape.ts";
import type { PuddleClass } from "./wetness.ts";

export { FULL_CUT, PUDDLE_CELL_PX, RIM_CUT, cutOf, fieldAt, fieldInto, peakOf, type Puddle } from "./puddle-shape.ts";

/**
 * Puddles (ADR-144 build note, P2.27-T20): where they lie on a map and what shape each is, from the map's ground and the
 * seed alone, so every page agrees and nothing uses `Math.random`. A puddle is a descriptor, a centre, a size class and a few
 * merged ellipses stretched along the path or the wall it lies by; its shape is a field of values from 0 to 1 that a page
 * rasterises when the puddle is on screen, and a puddle's fill is the level the field is cut at, so growing and drying only
 * move the cut and every shape on the way is a subset of the full one.
 *
 * A puddle is never clipped: its full shape is tested against the map's blocked cells with a margin, and a shape that
 * touches one is rebuilt smaller, then dropped.
 */

/**
 * The width of a collision cell in world pixels (`COMPILED_COLLISION_CELLS_PER_TILE` of them make a tile).
 */
export const BLOCK_CELL_PX = 4;

/**
 * The margin kept between a puddle and anything blocked, in world pixels.
 */
export const PUDDLE_MARGIN_PX = 8;


/**
 * The scales a puddle that touches something is rebuilt at before it is dropped.
 */
export const FIT_SCALES: readonly number[] = [1, 0.85, 0.7, 0.55];

/**
 * What puddles are placed on: the map's tiles by kind of ground and its blocked cells.
 *
 * `ground` holds one byte per tile: 0 where a puddle may not lie (water, no surface, the outside), 1 on soft ground (grass),
 * 2 on hard ground (dirt, stone, sand). `blocked` holds one byte per collision cell, `width * cells` across, 1 where a
 * character is stopped or a sprite or wall stands, so a puddle keeps clear of it.
 */
export interface PuddleGround {
  readonly tileSize: number;
  readonly cellsPerTile: number;
  readonly width: number;
  readonly height: number;
  readonly ground: Uint8Array;
  readonly blocked: Uint8Array;
}

/**
 * How puddles are placed: puddles go first on priority tiles (hard ground, a boundary between kinds of ground; the foot of a wall only raises a tile's score),
 * up to one for every `perPriorityTiles` of them and no two closer than `spacingTiles`; open grass then takes at most `openShare`
 * of that count, `openSpacingTiles` apart.
 */
export interface PlaceOptions {
  readonly perPriorityTiles: number;
  readonly spacingTiles: number;
  readonly openSpacingTiles: number;
  readonly openShare: number;
}

/**
 * The default placement.
 */
export const DEFAULT_PLACE: PlaceOptions = { perPriorityTiles: 6, spacingTiles: 4, openSpacingTiles: 8, openShare: 0.1 };

const SHIFTS: readonly number[] = [0, 24, -24];
const NO_SHIFT: readonly number[] = [0];
const CLASSES: readonly PuddleClass[] = ["small", "medium", "large"];
const DOMAIN_SCORE = 0x70756431;
const DOMAIN_LOW = 0x70756432;
const DOMAIN_CLASS = 0x70756433;
const LOW_CELL_TILES = 3;
const WINDOW_TILES = 5;

/**
 * Smooth low-frequency noise: the hashed values at the corners of a cell of `LOW_CELL_TILES` tiles blended across it, the dips
 * water would stand in.
 *
 * @param seed The seed.
 * @param tx Tile column.
 * @param ty Tile row.
 * @returns A value from 0 to 1.
 */
function lowNoise(seed: number, tx: number, ty: number): number {
  const gx = Math.floor(tx / LOW_CELL_TILES);
  const gy = Math.floor(ty / LOW_CELL_TILES);
  const fx = (tx - gx * LOW_CELL_TILES) / LOW_CELL_TILES;
  const fy = (ty - gy * LOW_CELL_TILES) / LOW_CELL_TILES;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = unit(seed, DOMAIN_LOW, gx + 0x8000, gy + 0x8000, 0);
  const b = unit(seed, DOMAIN_LOW, gx + 0x8001, gy + 0x8000, 0);
  const c = unit(seed, DOMAIN_LOW, gx + 0x8000, gy + 0x8001, 0);
  const d = unit(seed, DOMAIN_LOW, gx + 0x8001, gy + 0x8001, 0);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

/**
 * The map's blocked cells grown by the margin, so a point is clear when its cell is 0.
 *
 * @param ground The ground.
 * @returns One byte per collision cell, 1 within `PUDDLE_MARGIN_PX` of a blocked cell or outside the map.
 */
export function nearBlocked(ground: PuddleGround): Uint8Array {
  const columns = ground.width * ground.cellsPerTile;
  const rows = ground.height * ground.cellsPerTile;
  const reach = Math.ceil(PUDDLE_MARGIN_PX / BLOCK_CELL_PX);
  const across = new Uint8Array(columns * rows);
  for (let y = 0; y < rows; y += 1) {
    const row = y * columns;
    let last = -reach - 1;
    for (let x = 0; x < columns; x += 1) {
      if (ground.blocked[row + x] !== 0) last = x;
      if (x - last <= reach) across[row + x] = 1;
    }
    last = columns + reach + 1;
    for (let x = columns - 1; x >= 0; x -= 1) {
      if (ground.blocked[row + x] !== 0) last = x;
      if (last - x <= reach) across[row + x] = 1;
    }
  }
  const out = new Uint8Array(columns * rows);
  for (let x = 0; x < columns; x += 1) {
    let last = -reach - 1;
    for (let y = 0; y < rows; y += 1) {
      if (across[y * columns + x] !== 0) last = y;
      if (y - last <= reach) out[y * columns + x] = 1;
    }
    last = rows + reach + 1;
    for (let y = rows - 1; y >= 0; y -= 1) {
      if (across[y * columns + x] !== 0) last = y;
      if (last - y <= reach) out[y * columns + x] = 1;
    }
  }
  return out;
}

/**
 * How suited a tile is to a puddle: a hash, the dips, hard ground, a boundary between kinds of ground, a blocked neighbour
 * (the foot of a wall) and a penalty on open grass.
 *
 * @param ground The ground.
 * @param near The grown blocked cells (`nearBlocked`).
 * @param priority One byte per tile, written 1 where the tile is hard ground or on a boundary between kinds of ground.
 * @param seed The seed.
 * @param tx Tile column.
 * @param ty Tile row.
 * @returns The score, or -1 for a tile that cannot carry a puddle.
 */
function scoreOf(ground: PuddleGround, near: Uint8Array, priority: Uint8Array, seed: number, tx: number, ty: number): number {
  const w = ground.width;
  const kind = ground.ground[ty * w + tx] as number;
  if (kind === 0) return -1;
  const per = ground.cellsPerTile;
  const columns = w * per;
  const mid = (ty * per + (per >> 1)) * columns + tx * per + (per >> 1);
  if (near[mid] !== 0) return -1;
  let score = 0.4 * unit(seed, DOMAIN_SCORE, tx, ty, 0) + 0.6 * lowNoise(seed, tx, ty);
  if (kind === 2) score += 0.35;
  let boundary = false;
  let walled = false;
  for (let k = 0; k < 4; k += 1) {
    const nx = tx + (k === 0 ? -1 : k === 1 ? 1 : 0);
    const ny = ty + (k === 2 ? -1 : k === 3 ? 1 : 0);
    if (nx < 0 || ny < 0 || nx >= w || ny >= ground.height) continue;
    const other = ground.ground[ny * w + nx] as number;
    if (other !== kind && other !== 0) boundary = true;
    if (near[(ny * per + (per >> 1)) * columns + nx * per + (per >> 1)] !== 0) walled = true;
  }
  if (boundary) score += 0.4;
  if (walled) score += 0.45;
  priority[ty * w + tx] = kind === 2 || boundary ? 1 : 0;
  if (kind === 1 && !boundary && !walled) score -= 0.15;
  return score;
}

/**
 * The direction a puddle stretches in: the principal axis of the hard ground around it (a path or a road) or, against a
 * wall, of the blocked cells; a hashed angle with little stretch on open ground.
 *
 * @param ground The ground.
 * @param seed The seed.
 * @param tx Tile column.
 * @param ty Tile row.
 * @returns The axis angle and how elongated the evidence is, 1 for none.
 */
function axisAt(ground: PuddleGround, seed: number, tx: number, ty: number): { readonly angle: number; readonly stretch: number } {
  let n = 0;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  const fallback = unit(seed, DOMAIN_SHAPE, tx, ty, 99) * Math.PI;
  for (const mode of [2, 3]) {
    n = 0;
    sx = sy = sxx = syy = sxy = 0;
    for (let y = Math.max(0, ty - WINDOW_TILES); y <= Math.min(ground.height - 1, ty + WINDOW_TILES); y += 1) {
      for (let x = Math.max(0, tx - WINDOW_TILES); x <= Math.min(ground.width - 1, tx + WINDOW_TILES); x += 1) {
        const hit = mode === 2 ? ground.ground[y * ground.width + x] === 2 : tileBlocked(ground, x, y);
        if (!hit) continue;
        n += 1;
        sx += x;
        sy += y;
        sxx += x * x;
        syy += y * y;
        sxy += x * y;
      }
    }
    if (n < 6) continue;
    const mx = sx / n;
    const my = sy / n;
    const cxx = sxx / n - mx * mx;
    const cyy = syy / n - my * my;
    const cxy = sxy / n - mx * my;
    const half = (cxx + cyy) / 2;
    const spread = Math.sqrt(((cxx - cyy) / 2) ** 2 + cxy * cxy);
    const major = half + spread;
    const minor = Math.max(half - spread, 1e-6);
    if (major / minor < 1.3) continue;
    return { angle: 0.5 * Math.atan2(2 * cxy, cxx - cyy), stretch: Math.min(1.45, 1 + Math.log2(major / minor) * 0.18) };
  }
  return { angle: fallback, stretch: 1 };
}

/**
 * Whether a tile holds any blocked cell.
 *
 * @param ground The ground.
 * @param tx Tile column.
 * @param ty Tile row.
 * @returns True when a cell of the tile is blocked.
 */
function tileBlocked(ground: PuddleGround, tx: number, ty: number): boolean {
  const per = ground.cellsPerTile;
  const columns = ground.width * per;
  for (let y = 0; y < per; y += 2) {
    for (let x = 0; x < per; x += 2) {
      if (ground.blocked[(ty * per + y) * columns + tx * per + x] !== 0) return true;
    }
  }
  return false;
}

/**
 * The size class of a puddle: a hash nudged toward large on hard ground and against walls.
 *
 * @param roll The tile's hash, 0 to 1.
 * @param hard Whether the ground is hard.
 * @param walled Whether a wall stands by.
 * @returns The class.
 */
function classOf(roll: number, hard: boolean, walled: boolean): PuddleClass {
  const nudged = roll + (hard ? 0.2 : 0) + (walled ? 0.12 : 0) - (!hard && !walled ? 0.15 : 0);
  return nudged < 0.35 ? "small" : nudged < 0.7 ? "medium" : "large";
}

/**
 * Whether a puddle's full shape lies whole on open ground: every cell above the cut is on ground, clear of every blocked
 * cell and the margin, and not on another puddle.
 *
 * @param puddle The puddle.
 * @param ground The ground.
 * @param near The grown blocked cells.
 * @param taken The cells earlier puddles hold.
 * @param onHard Whether the puddle is centred on hard ground, in which case it keeps to it: no more than a third of it may lie on soft ground.
 * @returns True when it fits.
 */
function fits(puddle: Puddle, ground: PuddleGround, near: Uint8Array, taken: Uint8Array, onHard: boolean): boolean {
  const per = ground.cellsPerTile;
  const columns = ground.width * per;
  const rows = ground.height * per;
  const x0 = puddle.x - puddle.radius;
  const y0 = puddle.y - puddle.radius;
  const gx = Math.round(x0 / PUDDLE_CELL_PX);
  const gy = Math.round(y0 / PUDDLE_CELL_PX);
  const tilePx = ground.tileSize;
  const blobs = puddle.blobs;
  for (let i = 0; i < blobs.length; i += 4) {
    for (let k = 0; k < 5; k += 1) {
      const u = (blobs[i] as number) + (k === 1 ? (blobs[i + 2] as number) : k === 2 ? -(blobs[i + 2] as number) : 0);
      const w = (blobs[i + 1] as number) + (k === 3 ? (blobs[i + 3] as number) : k === 4 ? -(blobs[i + 3] as number) : 0);
      const px = puddle.x + u * puddle.cos - w * puddle.sin;
      const py = puddle.y + u * puddle.sin + w * puddle.cos;
      const bx = Math.floor(px / BLOCK_CELL_PX);
      const by = Math.floor(py / BLOCK_CELL_PX);
      if (bx < 0 || by < 0 || bx >= columns || by >= rows || near[by * columns + bx] !== 0 || taken[by * columns + bx] !== 0) return false;
    }
  }
  let total = 0;
  let soft = 0;
  for (let cy = 0; cy < puddle.radius; cy += 1) {
    for (let cx = 0; cx < puddle.radius; cx += 1) {
      const px = x0 + cx * PUDDLE_CELL_PX + 1;
      const py = y0 + cy * PUDDLE_CELL_PX + 1;
      const value = fieldAt(puddle, px, py, gx + cx, gy + cy);
      if (value < RIM_CUT) continue;
      const bx = Math.floor(px / BLOCK_CELL_PX);
      const by = Math.floor(py / BLOCK_CELL_PX);
      if (bx < 0 || by < 0 || bx >= columns || by >= rows) return false;
      if (near[by * columns + bx] !== 0 || taken[by * columns + bx] !== 0) return false;
      const kind = ground.ground[Math.floor(py / tilePx) * ground.width + Math.floor(px / tilePx)] as number;
      if (kind === 0) return false;
      if (value >= FULL_CUT) {
        total += 1;
        if (kind === 1) soft += 1;
      }
    }
  }
  return !onHard || soft * 3 <= total;
}

/**
 * Marks the cells a puddle holds so a later puddle keeps off it.
 *
 * @param puddle The puddle.
 * @param ground The ground.
 * @param taken The cells to mark.
 */
function take(puddle: Puddle, ground: PuddleGround, taken: Uint8Array): void {
  const columns = ground.width * ground.cellsPerTile;
  const rows = ground.height * ground.cellsPerTile;
  const x0 = puddle.x - puddle.radius;
  const y0 = puddle.y - puddle.radius;
  const gx = Math.round(x0 / PUDDLE_CELL_PX);
  const gy = Math.round(y0 / PUDDLE_CELL_PX);
  for (let cy = 0; cy < puddle.radius; cy += 1) {
    for (let cx = 0; cx < puddle.radius; cx += 1) {
      const px = x0 + cx * PUDDLE_CELL_PX + 1;
      const py = y0 + cy * PUDDLE_CELL_PX + 1;
      if (fieldAt(puddle, px, py, gx + cx, gy + cy) < RIM_CUT) continue;
      const bx = Math.floor(px / BLOCK_CELL_PX);
      const by = Math.floor(py / BLOCK_CELL_PX);
      if (bx >= 0 && by >= 0 && bx < columns && by < rows) taken[by * columns + bx] = 1;
    }
  }
}

/**
 * Places the puddles of a map: tiles scored, the best that are local maxima taken highest first at least `spacingTiles`
 * apart up to the share the ground earns, each given a class, an axis and a shape, and rebuilt smaller or dropped when it
 * would be clipped.
 *
 * @param ground The map's ground and blocked cells.
 * @param seed The world's seed.
 * @param mapId The map's id, part of every puddle's key.
 * @param options The density and spacing.
 * @returns The puddles in placement order.
 */
export function placePuddles(ground: PuddleGround, seed: number, mapId: string, options: PlaceOptions = DEFAULT_PLACE): Puddle[] {
  const steps = placePuddleSteps(ground, seed, mapId, Number.POSITIVE_INFINITY, options);
  let next = steps.next();
  while (next.done !== true) {
    next = steps.next();
  }
  return next.value;
}

/**
 * `placePuddles` in slices (ADR-147 decision 18): the same work in the same order, yielding after every `slice` tiles
 * scored or candidates tried, so a page can place the puddles of a map a crossing reaches a few milliseconds a frame while
 * the world stays shown. What it returns is exactly what `placePuddles` returns.
 *
 * @param ground The map's ground and blocked cells.
 * @param seed The world's seed.
 * @param mapId The map's id, part of every puddle's key.
 * @param slice How many tiles or candidates between two yields.
 * @param options The density and spacing.
 * @returns A generator that yields between slices and returns the puddles in placement order.
 */
export function* placePuddleSteps(ground: PuddleGround, seed: number, mapId: string, slice: number, options: PlaceOptions = DEFAULT_PLACE): Generator<void, Puddle[], void> {
  const near = nearBlocked(ground);
  const taken = new Uint8Array(near.length);
  const w = ground.width;
  const h = ground.height;
  const scores = new Float32Array(w * h).fill(-1);
  const priority = new Uint8Array(w * h);
  let work = 0;
  for (let ty = 0; ty < h; ty += 1) {
    for (let tx = 0; tx < w; tx += 1) {
      scores[ty * w + tx] = scoreOf(ground, near, priority, seed, tx, ty);
      work += 1;
      if (work >= slice) {
        work = 0;
        yield;
      }
    }
  }
  const chosen: number[] = [];
  const open: number[] = [];
  for (let i = 0; i < scores.length; i += 1) {
    if ((scores[i] as number) < 0) continue;
    if (priority[i] === 1) chosen.push(i);
    else open.push(i);
  }
  const bySuit = (a: number, b: number): number => (scores[b] as number) - (scores[a] as number) || a - b;
  chosen.sort(bySuit);
  open.sort(bySuit);
  const placed: Puddle[] = [];
  const buckets = new Map<number, number[]>();
  function* sweep(list: readonly number[], spacingTiles: number, limit: number): Generator<void, void, void> {
    const spacing2 = spacingTiles * spacingTiles;
    const start = placed.length;
    for (const index of list) {
      if (placed.length - start >= limit) break;
      work += 1;
      if (work >= slice) {
        work = 0;
        yield;
      }
      const tx = index % w;
      const ty = (index - tx) / w;
      if (crowdedAt(buckets, w, tx, ty, spacing2)) continue;
      const puddle = fitAt(ground, near, taken, seed, mapId, index, placed.length);
      if (puddle === null) continue;
      take(puddle, ground, taken);
      placed.push(puddle);
      const key = (ty >> 3) * 0x10000 + (tx >> 3);
      const bucket = buckets.get(key);
      if (bucket === undefined) buckets.set(key, [index]);
      else bucket.push(index);
    }
  }
  yield* sweep(chosen, options.spacingTiles, Math.ceil(chosen.length / options.perPriorityTiles));
  yield* sweep(open, options.openSpacingTiles, Math.ceil(placed.length * options.openShare));
  return placed;
}

/**
 * Whether another puddle's tile lies within a distance of a tile.
 *
 * @param buckets The placed tiles by bucket of 8 by 8 tiles.
 * @param width The map's width in tiles.
 * @param tx Tile column.
 * @param ty Tile row.
 * @param spacing2 The least distance, squared, in tiles.
 * @returns True when one lies too close.
 */
function crowdedAt(buckets: ReadonlyMap<number, readonly number[]>, width: number, tx: number, ty: number, spacing2: number): boolean {
  for (let by = (ty >> 3) - 1; by <= (ty >> 3) + 1; by += 1) {
    for (let bx = (tx >> 3) - 1; bx <= (tx >> 3) + 1; bx += 1) {
      for (const other of buckets.get(by * 0x10000 + bx) ?? []) {
        const ox = other % width;
        const oy = (other - ox) / width;
        if ((ox - tx) ** 2 + (oy - ty) ** 2 < spacing2) return true;
      }
    }
  }
  return false;
}

/**
 * Builds the puddle for a tile: its class, axis and shape, tried at each fit scale and offset along the axis, then in each
 * smaller class, until one lies whole.
 *
 * @param ground The ground.
 * @param near The grown blocked cells.
 * @param taken The cells earlier puddles hold.
 * @param seed The seed.
 * @param mapId The map's id.
 * @param index The tile's index.
 * @param id The puddle's index.
 * @returns The puddle, or null when none fits.
 */
function fitAt(ground: PuddleGround, near: Uint8Array, taken: Uint8Array, seed: number, mapId: string, index: number, id: number): Puddle | null {
  const w = ground.width;
  const tx = index % w;
  const ty = (index - tx) / w;
  const kind = ground.ground[index] as number;
  const cls = classOf(unit(seed, DOMAIN_CLASS, tx, ty, 0), kind === 2, tileBlockedNear(ground, near, tx, ty));
  const { angle, stretch } = axisAt(ground, seed, tx, ty);
  const onset = unit(seed, DOMAIN_CLASS, tx, ty, 1);
  const baseX = (tx + 0.5) * ground.tileSize + (unit(seed, DOMAIN_CLASS, tx, ty, 2) - 0.5) * ground.tileSize * 0.6;
  const baseY = (ty + 0.5) * ground.tileSize + (unit(seed, DOMAIN_CLASS, tx, ty, 3) - 0.5) * ground.tileSize * 0.6;
  const even = (v: number): number => Math.round(v / (2 * PUDDLE_CELL_PX)) * 2 * PUDDLE_CELL_PX;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  for (let rank = CLASSES.indexOf(cls); rank >= 0; rank -= 1) {
    for (const scale of FIT_SCALES) {
      for (const shift of rank === CLASSES.indexOf(cls) ? SHIFTS : NO_SHIFT) {
        const puddle = shapeOf(seed, id, `${mapId}:${tx}:${ty}`, CLASSES[rank] as PuddleClass, stretch, angle, scale, even(baseX + cos * shift), even(baseY + sin * shift), onset);
        if (fits(puddle, ground, near, taken, kind === 2)) return puddle;
      }
    }
  }
  return null;
}

/**
 * Whether the cell at the middle of one of a tile's four neighbours is within the margin of something blocked, which is to
 * say the tile lies by a wall.
 *
 * @param ground The ground.
 * @param near The grown blocked cells.
 * @param tx Tile column.
 * @param ty Tile row.
 * @returns True against a wall.
 */
function tileBlockedNear(ground: PuddleGround, near: Uint8Array, tx: number, ty: number): boolean {
  const per = ground.cellsPerTile;
  const columns = ground.width * per;
  for (let k = 0; k < 4; k += 1) {
    const nx = tx + (k === 0 ? -2 : k === 1 ? 2 : 0);
    const ny = ty + (k === 2 ? -2 : k === 3 ? 2 : 0);
    if (nx < 0 || ny < 0 || nx >= ground.width || ny >= ground.height) continue;
    if (near[(ny * per + (per >> 1)) * columns + nx * per + (per >> 1)] !== 0) return true;
  }
  return false;
}
